import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import { PositionTile } from '@/components/PositionTile';
import { PositionDetailDrawer } from '@/components/PositionDetailDrawer';
import { PositionTradeDrawer } from '@/components/PositionTradeDrawer';
import { TickerSymbolDrawer } from '@/components/TickerSymbolDrawer';
import { ExchangeFilterDrawer, ExchangeFilterOption } from '@/components/ExchangeFilterDrawer';
import { useOpenPositions } from '@/hooks/usePositions';
import { useNavigate } from 'react-router-dom';
import { PositionData, ClosedPositionData } from "@crypto-trading/common/browser";
import { Loader2, Plus, ChevronDown, BarChart3 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExchangesStripCard } from '@/components/ExchangesStripCard';
import { TradeStatsCards } from '@/components/TradeStatsCards';
import { EmptyState } from '@/components/ui/EmptyState';
import { ExchangeLogo } from '@/components/ui/ExchangeLogo';
import { ReferralBottomNav } from '@/components/ReferralBottomNav';
import { AssetClassToggle, type AssetClass } from '@/components/AssetClassToggle';
import { StockTickerDrawer } from '@/components/StockTickerDrawer';
import { useStockInstruments } from '@/hooks/useStockNews';
import { buildStockTradeTarget, isStockPosition, type StockTradeTarget } from '@/stock/stock-trade';
import { trackEvent } from '@/utils/analytics';

export const AllPositionsPageMobile: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || '';
  
  const navigate = useNavigate();
  const [selectedPosition, setSelectedPosition] = useState<PositionData | ClosedPositionData | null>(null);
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [exchangeFilter, setExchangeFilter] = useState<ExchangeFilterOption>('all');
  const [assetClass, setAssetClass] = useState<AssetClass>('crypto');
  const [stockTickerOpen, setStockTickerOpen] = useState(false);
  const [stockTradeTarget, setStockTradeTarget] = useState<StockTradeTarget | null>(null);
  const [isExchangeFilterDrawerOpen, setIsExchangeFilterDrawerOpen] = useState(false);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef<number | null>(null);
  const isPulling = useRef(false);

  // React Query hooks
  const exchangeCodeFilter = exchangeFilter === 'all' ? undefined : exchangeFilter;
  const { data: openPositions = [], isLoading: isLoadingOpen, refetch: refetchOpen } = useOpenPositions(
    userId,
    true,
    exchangeCodeFilter
  );

  const positions = openPositions;
  const { data: stockInstruments = [], isSuccess: stockCatalogReady } = useStockInstruments();
  const stockTickers = useMemo(
    () => new Set(stockInstruments.map((row) => row.ticker)),
    [stockInstruments]
  );
  const stockTickerList = useMemo(() => [...stockTickers].sort(), [stockTickers]);
  const visiblePositions = positions.filter((position) => {
    if (!stockCatalogReady) return assetClass === 'crypto';
    const stock = isStockPosition(position.symbol, stockTickers);
    return assetClass === 'stocks' ? stock : !stock;
  });
  const isLoading = isLoadingOpen;
  const visibleOpenPositions = openPositions.filter((position) => {
    if (!stockCatalogReady) return assetClass === 'crypto';
    const stock = isStockPosition(position.symbol, stockTickers);
    return assetClass === 'stocks' ? stock : !stock;
  });
  const showEmpty = !isLoading && visiblePositions.length === 0;

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetchOpen();
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [isRefreshing, refetchOpen]);

  // Pull-to-refresh handlers
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const getPageScrollTop = () => {
      const currentWindow = typeof globalThis === 'undefined' ? undefined : globalThis.window;
      if (!currentWindow) return 0;
      return currentWindow.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    };

    const isAtTop = () => {
      const containerTop = container.scrollTop;
      const pageTop = getPageScrollTop();
      return containerTop <= 0 && pageTop <= 0;
    };

    const resetPullState = () => {
      isPulling.current = false;
      touchStartY.current = null;
      setPullDistance(0);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (isAtTop()) {
        touchStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      } else {
        resetPullState();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || touchStartY.current === null) return;
      if (!isAtTop()) {
        resetPullState();
        return;
      }
      const currentY = e.touches[0].clientY;
      const distance = currentY - touchStartY.current;
      if (distance > 0) {
        e.preventDefault();
        const maxPull = 40;
        setPullDistance(Math.min(distance, maxPull));
      } else {
        resetPullState();
      }
    };

    const handleTouchEnd = () => {
      if (isPulling.current && pullDistance > 35 && !isRefreshing) {
        handleRefresh();
      } else {
        setPullDistance(0);
      }
      touchStartY.current = null;
      isPulling.current = false;
    };

    const handleTouchCancel = () => {
      resetPullState();
    };

    const handleScroll = () => {
      if (!isAtTop()) {
        resetPullState();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchCancel);
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [pullDistance, isRefreshing, handleRefresh]);

  const handleOpenTickerDrawer = () => {
    trackEvent('custom_trade_attempt', { page: 'positions', userId });
    if (assetClass === 'stocks') {
      setStockTickerOpen(true);
      return;
    }
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setTradeSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  const handleTradeDrawerClose = (open: boolean) => {
    setIsTradeDrawerOpen(open);
    if (!open) {
      setTradeSymbol(null);
      setStockTradeTarget(null);
    }
  };

  const handleStockTickerSelect = useCallback((ticker: string) => {
    const target = buildStockTradeTarget(ticker, stockInstruments);
    setStockTradeTarget(target);
    setTradeSymbol(target.symbol);
    setStockTickerOpen(false);
    setIsTradeDrawerOpen(true);
  }, [stockInstruments]);

  const getExchangeDisplayName = (exchange: ExchangeFilterOption): string => {
    const exchangeMap: Record<ExchangeFilterOption, string> = {
      all: 'All Exchanges',
      bybit: 'Bybit',
      binance: 'Binance',
      gate: 'Gate.io',
      aster: 'Aster',
      kucoin: 'KuCoin',
      hyperliquid: 'Hyperliquid',
    };
    return exchangeMap[exchange] || exchange;
  };

  return (
    <div className="min-h-screen flex flex-col">     
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col gap-4 px-4 pt-4 pb-2">
            <PageHeader
              title="Trade"
              subtitle="Manage and monitor your open positions across all your connected exchanges."
            />
            <ExchangesStripCard userId={userId} logoSize={34} />
            <div className="flex items-center gap-2">
              <div className="w-[146px] shrink-0">
                <AssetClassToggle value={assetClass} onChange={setAssetClass} />
              </div>
              <button
                type="button"
                onClick={() => setIsExchangeFilterDrawerOpen(true)}
                aria-label="Filter by exchange"
                className="glass ml-auto flex h-11 min-w-0 items-center gap-2 !rounded-lg pl-2 pr-3 text-sm font-semibold text-[#fafafa]"
              >
                {exchangeFilter === 'all' ? (
                  <img src="/logo-mark.svg" alt="" width={22} height={18} />
                ) : (
                  <ExchangeLogo code={exchangeFilter} size={20} />
                )}
                <span className="truncate">{getExchangeDisplayName(exchangeFilter)}</span>
                <ChevronDown className="size-4 shrink-0 text-[#a3a3a3]" />
              </button>
            </div>
            <TradeStatsCards userId={userId} positions={visibleOpenPositions} layout="grid" />
            {/* Pull-to-refresh indicator */}
            <div 
              className="flex justify-center items-center overflow-hidden transition-all duration-300 ease-out"
              style={{
                transform: `translateY(${Math.max(0, pullDistance - 20)}px)`,
                opacity: (pullDistance > 0 || isRefreshing) ? Math.min(1, pullDistance / 35) : 0,
                maxHeight: (pullDistance > 0 || isRefreshing) ? '3rem' : '0',
                paddingTop: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                paddingBottom: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                marginBottom: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                pointerEvents: (pullDistance > 0 || isRefreshing) ? 'auto' : 'none',
                transition: pullDistance === 0 
                  ? 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), max-height 0.3s cubic-bezier(0.16, 1, 0.3, 1), padding 0.3s cubic-bezier(0.16, 1, 0.3, 1), margin 0.3s cubic-bezier(0.16, 1, 0.3, 1)' 
                  : 'opacity 0.2s ease-out',
              }}
            >
              {isRefreshing ? (
                <Loader2 className="w-5 h-5 animate-spin text-accent" />
              ) : (
                <div className="text-xs text-text-secondary">
                  {pullDistance > 35 ? 'Release to refresh' : 'Pull to refresh'}
                </div>
              )}
            </div>
          </div>
          
          <div 
            className="px-4 pb-4 transition-transform duration-300 ease-out"
            style={{
              transform: pullDistance > 0 ? `translateY(${Math.min(pullDistance * 0.3, 20)}px)` : 'translateY(0)',
            }}
          >
            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            )}

            {/* Positions List */}
            {showEmpty && (
              <EmptyState
                compact
                title="No Open Positions Yet"
                description="Connect an exchange and start trading to see your open positions here."
                action={
                  <button
                    type="button"
                    onClick={handleOpenTickerDrawer}
                    className="flex h-11 w-[156px] items-center justify-center gap-1.5 rounded-xl border-2 border-white/[0.12] bg-[#07a66c] font-semibold text-white"
                  >
                    <Plus className="size-5" />
                    <span>New Trade</span>
                  </button>
                }
              />
            )}
            {!isLoading && (
              <div className="space-y-3">
                {visiblePositions.map((position) => (
                  <PositionTile
                    key={position.positionId}
                    position={position}
                    onClick={() => setSelectedPosition(position)}
                  />
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <button
        onClick={() => navigate('/statistics')}
        className="fixed bottom-40 right-4 z-40 w-14 h-14 rounded-full bg-primary border-2 border-accent hover:bg-primary/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="View statistics"
      >
        <BarChart3 className="w-6 h-6 text-accent" />
      </button>

      {!showEmpty && (
      <button
        onClick={handleOpenTickerDrawer}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-accent hover:bg-accent/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Open new trade"
      >
        <Plus className="w-6 h-6 text-white" />
      </button>
      )}

      <ReferralBottomNav />

      <TickerSymbolDrawer
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Enter Ticker Symbol"
        submitButtonText="Continue to Trade"
      />

      <ExchangeFilterDrawer
        open={isExchangeFilterDrawerOpen}
        onOpenChange={setIsExchangeFilterDrawerOpen}
        selectedExchange={exchangeFilter}
        onSelect={setExchangeFilter}
      />

      <StockTickerDrawer
        open={stockTickerOpen}
        onOpenChange={setStockTickerOpen}
        tickers={stockTickerList}
        onSelect={handleStockTickerSelect}
      />

      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        displayTicker={stockTradeTarget?.displayTicker}
        allowedExchangeCodes={stockTradeTarget?.allowedExchangeCodes}
        symbolByExchangeCode={stockTradeTarget?.symbolByExchangeCode}
        open={isTradeDrawerOpen}
        onOpenChange={handleTradeDrawerClose}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
            setIsTradeDrawerOpen(false);
          }
        }}
      />

      <PositionDetailDrawer
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) => setSelectedPosition(updatedPosition)}
        source="positions_page"
      />
    </div>
  );
};
